import React, { useEffect, useState, useMemo } from "react";
// ANT DESIGN COMPONENTS
import { Divider, Empty, Tooltip } from "antd";
// I18N TRANSLATION
import { useTranslation } from "react-i18next";
// FONT AWESOME LIBRARY AND ICONS
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faClock,
  faMouse,
  faStopwatch,
  faToggleOn,
} from "@fortawesome/free-solid-svg-icons";
// REDUX
import { useSelector } from "react-redux";
import { useLazyGetBankQuery } from "../../../redux/banks/bankAPI";
// CUSTOM HOOKS
import { useLastLiftMessage } from "../socketMessageState/BankMessagesContext";
// COMPONENTS
import ActionButtonToggle from "../actionButtons/ActionButtonToggle";
import ActionButtonOnRelease from "../actionButtons/ActionButtonOnRelease";
import ActionButtonTimed from "../actionButtons/ActionButtonTimed";
import ActionButtonEnergizeTurnback from "../actionButtons/ActionButtonEnergizeTurnback";
import ActionButtonToggleTimer from "../actionButtons/ActionButtonToggleTimer";
import ActionButtonSwitch from "../actionButtons/ActionButtonSwitch";
import MonitoringHeartBeat from "../monitoring/MonitoringHeartBeat";
// AUTHORIZATION
import GETJwtToken from "../../../redux/authentication/authentication";
// INTERFACES
import { RootState } from "../../../app/store";
import { SocketMessageInterface } from "../../../interfaces/Monitoring.interface";
import { BankActionInterface } from "../../../interfaces/Bank.interface";
// UTILS
import { getServiceModeDisplay } from "../../../utils/serviceModeDisplay";
// CONSTANTS
import { BLANK_SOCKET_MESSAGE } from "../../../constants/monitoring";
// UUID
import { stringToUuidT } from "../../../utils/uuid";

export default function ControlPanelLiftActions({
  isControlPanel,
  bankId,
  bankName,
}: {
  isControlPanel: boolean;
  bankId: string;
  bankName: string;
}) {
  // ************************************************ */
  // GLOBAL VARIABLES ******************************* */
  const [t] = useTranslation("global");

  // ************************************************ */
  // USE STATE VARIABLES **************************** */
  const [actions, setActions] = useState<BankActionInterface[]>([]);
  const [displayToggle, setDisplayToggle] = useState<boolean>(false);
  const [displayHoldback, setDisplayHoldback] = useState<boolean>(false);
  const [displayTurnback, setDisplayTurnback] = useState<boolean>(false);
  const [disableTurnBack, setDisabledTurnBack] = useState<boolean>(false);
  const [displayEnergizerTurnback, setDisplayEnergizerTurnback] =
    useState<boolean>(false);
  const [displayToggleTimer, setDisplayToggleTimer] = useState<boolean>(false);
  const [displaySwitch, setDisplaySwitch] = useState<boolean>(false);

  // ************************************************ */
  // REDUX SLICE VARIABLES ************************** */
  const { theme } = useSelector((state: RootState) => state.home);
  const { liftSelected, bankSelected } = useSelector(
    (state: RootState) => state.monitoring
  );

  // ************************************************ */
  // SERVICES AND API CALLS ************************* */
  const [triggerGetBank] = useLazyGetBankQuery();

  // ************************************************ */
  // CUSTOM HOOKS *********************************** */
  const liftUuid = useMemo(
    () => stringToUuidT(liftSelected.id),
    [liftSelected.id]
  );
  const socketMessageLift: SocketMessageInterface =
    useLastLiftMessage(liftUuid) || BLANK_SOCKET_MESSAGE;

  const carServiceString = useMemo(() => {
    const info = getServiceModeDisplay(
      socketMessageLift.serviceMode?.messageType ?? null,
      socketMessageLift.serviceMode?.message?.message ?? null
    );
    return info?.displayName ?? 0;
  }, [socketMessageLift.serviceMode]);

  // ************************************************ */
  // FUNCTIONS ************************************** */
  const getBank = async () => {
    const token = await GETJwtToken();
    try {
      const result = await triggerGetBank({
        bankId,
        token,
      }).unwrap();
      if (result && result.data && result.data.actions) {
        setActions(result.data.actions);
      }
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("Error", err);
    }
  };

  // ************************************************ */
  // USE EFFECT ************************************* */
  useEffect(() => {
    if (actions && actions.length > 0) {
      actions.map((action: any) => {
        if (action && action.button_type && action.button_type === "TOGGLE") {
          setDisplayToggle(true);
        }
        if (action && action.button_type && action.button_type === "HOLDBACK") {
          setDisplayHoldback(true);
        }
        if (action && action.button_type && action.button_type === "TURNBACK") {
          setDisplayTurnback(true);
        }
        if (
          action &&
          action.button_type &&
          action.button_type === "ENERGIZER_TURN_BACK"
        ) {
          setDisplayEnergizerTurnback(true);
        }
        if (
          action &&
          action.button_type &&
          action.button_type === "TOGGLETIMER"
        ) {
          setDisplayToggleTimer(true);
        }
        if (action && action.button_type && action.button_type === "SWITCH") {
          setDisplaySwitch(true);
        }
        return true;
      });
    }
  }, [actions]);

  useEffect(() => {
    if (isControlPanel && bankSelected) {
      setActions(bankSelected.actions);
    } else if (!isControlPanel) {
      getBank();
    }
  }, [isControlPanel]);

  // ************************************************ */
  // COMPONENT ************************************** */
  return (
    <div
      className="monitoringView__detailsBox"
      style={{ minHeight: "400px", height: "auto" }}
    >
      <div
        className={`monitoringView__controlPanel monitoringView__controlPanel__${theme}`}
        style={{
          minHeight: "400px",
          height: "auto",
          paddingBottom: "25px",
        }}
      >
        {!isControlPanel && (
          <div className="monitoring__card__header mb-2">
            <div className="generalStyles__flex">
              <MonitoringHeartBeat bankId={bankId} />
              <div className="generalStyles__textEllipsis" role="button">
                <Tooltip
                  placement="topLeft"
                  title={bankName || bankSelected.name || ""}
                >
                  <h6>{bankName || bankSelected.name || ""}</h6>
                </Tooltip>
              </div>
            </div>
          </div>
        )}
        {actions && actions.length > 0 ? (
          <>
            {/** ******************************************************** */}
            {/** TOGGLE BUTTON  */}
            {displayToggle && (
              <div className={`drawer__box__control__panel__${theme} mt-2`}>
                <div>
                  <Divider
                    orientation="left"
                    className="generalStyles__drawerDividerSuccess"
                  >
                    <h5>
                      <FontAwesomeIcon
                        icon={faToggleOn}
                        className="generalStyles__success generalStyles__mrFix"
                      />
                      TOGGLE
                    </h5>
                  </Divider>
                </div>
                {actions && (
                  <div>
                    {actions.map(
                      (action: BankActionInterface, index: number) => (
                        <div
                          className="generalStyles__flexCenter mt-1"
                          key={index}
                        >
                          {action &&
                            action.button_type &&
                            action.button_type === "TOGGLE" && (
                              <ActionButtonToggle
                                validStates={action.valid_states}
                                toggleValidStates={action.toggle_valid_states}
                                label={action.label}
                                toggleLabel={action.toggle_label}
                                messageId={action.message_id}
                                toggleMessageId={action.toggle_message_id}
                                colorAction={action.color}
                                colorToggleAction={action.toggle_color}
                                carService={Number(carServiceString)}
                              />
                            )}
                        </div>
                      )
                    )}
                  </div>
                )}
              </div>
            )}
            {/** ******************************************************** */}
            {/** HOLDBACK BUTTON  */}
            {displayHoldback && (
              <div
                className={`drawer__box__control__panel__${theme} mt-3`}
                style={{ width: "100%" }}
              >
                <div>
                  <Divider
                    orientation="left"
                    className="generalStyles__drawerDivider"
                  >
                    <h5>
                      <FontAwesomeIcon
                        icon={faMouse}
                        className="generalStyles__info generalStyles__mrFix"
                      />
                      HOLDBACK
                    </h5>
                  </Divider>
                </div>
                {actions && (
                  <div>
                    {actions
                      .slice()
                      .sort((a: BankActionInterface, b: BankActionInterface) =>
                        a.created_at.localeCompare(b.created_at)
                      )
                      .map((action: BankActionInterface, index: number) => (
                        <div className="generalStyles__flexCenter" key={index}>
                          {action &&
                            action.button_type &&
                            action.button_type === "HOLDBACK" && (
                              <ActionButtonOnRelease
                                validStates={action.valid_states}
                                toggleValidStates={action.toggle_valid_states}
                                label={action.label}
                                toggleLabel={action.toggle_label}
                                messageId={action.message_id}
                                toggleMessageId={action.toggle_message_id}
                                colorAction={action.color}
                                timerAction={action.timer}
                                carService={Number(carServiceString)}
                              />
                            )}
                        </div>
                      ))}
                  </div>
                )}
              </div>
            )}
            {/** ******************************************************** */}
            {/** TURNBACK BUTTON  */}
            {displayTurnback && (
              <div
                className={`drawer__box__control__panel__${theme} mt-3`}
                style={{ width: "100%" }}
              >
                <div>
                  <Divider
                    orientation="left"
                    className="generalStyles__drawerDividerWarning"
                  >
                    <h5>
                      <FontAwesomeIcon
                        icon={faClock}
                        className="generalStyles__warning generalStyles__mrFix"
                      />
                      TURN BACK
                    </h5>
                  </Divider>
                </div>
                {actions && (
                  <div>
                    {actions
                      .slice()
                      .sort((a: BankActionInterface, b: BankActionInterface) =>
                        a.created_at.localeCompare(b.created_at)
                      )
                      .map((action: BankActionInterface, index: number) => (
                        <div className="generalStyles__flexCenter" key={index}>
                          {action &&
                            action.button_type &&
                            action.button_type === "TURNBACK" && (
                              <ActionButtonTimed
                                validStates={action.valid_states}
                                toggleValidStates={action.toggle_valid_states}
                                label={action.label}
                                toggleLabel={action.toggle_label}
                                messageId={action.message_id}
                                toggleMessageId={action.toggle_message_id}
                                colorAction={action.color}
                                timerAction={action.timer}
                                carService={Number(carServiceString)}
                                disableButton={disableTurnBack}
                                setDisableButton={setDisabledTurnBack}
                              />
                            )}
                        </div>
                      ))}
                  </div>
                )}
              </div>
            )}
            {/** ******************************************************** */}
            {/** ENERGIZER TURNBACK BUTTON  */}
            {displayEnergizerTurnback && (
              <div
                className={`drawer__box__control__panel__${theme} mt-3`}
                style={{ width: "100%" }}
              >
                <div>
                  <Divider
                    orientation="left"
                    className="generalStyles__drawerDividerWarning"
                  >
                    <h5>
                      <FontAwesomeIcon
                        icon={faClock}
                        className="generalStyles__warning generalStyles__mrFix"
                      />
                      ENERGIZER TURN BACK
                    </h5>
                  </Divider>
                </div>
                {actions && (
                  <div>
                    {actions
                      .slice()
                      .sort((a: BankActionInterface, b: BankActionInterface) =>
                        a.created_at.localeCompare(b.created_at)
                      )
                      .map((action: BankActionInterface, index: number) => (
                        <div className="generalStyles__flexCenter" key={index}>
                          {action &&
                            action.button_type &&
                            action.button_type === "ENERGIZER_TURN_BACK" && (
                              <ActionButtonEnergizeTurnback
                                validStates={action.valid_states}
                                toggleValidStates={action.toggle_valid_states}
                                label={action.label}
                                toggleLabel={action.toggle_label}
                                messageId={action.message_id}
                                toggleMessageId={action.toggle_message_id}
                                colorAction={action.color}
                                timerAction={action.timer}
                                carService={Number(carServiceString)}
                              />
                            )}
                        </div>
                      ))}
                  </div>
                )}
              </div>
            )}
            {/** ******************************************************** */}
            {/** TOGGLE TIMER BUTTON  */}
            {displayToggleTimer && (
              <div
                className={`drawer__box__control__panel__${theme} mt-3`}
                style={{ width: "100%" }}
              >
                <div>
                  <Divider
                    orientation="left"
                    className="generalStyles__drawerDividerError"
                  >
                    <h5>
                      <FontAwesomeIcon
                        icon={faStopwatch}
                        className="generalStyles__mrFix generalStyles__error"
                      />
                      TOGGLE TIMER
                    </h5>
                  </Divider>
                </div>
                {actions && (
                  <div>
                    {actions
                      .slice()
                      .sort((a: BankActionInterface, b: BankActionInterface) =>
                        a.created_at.localeCompare(b.created_at)
                      )
                      .map((action: BankActionInterface, index: number) => (
                        <div className="generalStyles__flexCenter" key={index}>
                          {action &&
                            action.button_type &&
                            action.button_type === "TOGGLETIMER" && (
                              <ActionButtonToggleTimer
                                validStates={action.valid_states}
                                toggleValidStates={action.toggle_valid_states}
                                label={action.label}
                                toggleLabel={action.toggle_label}
                                messageId={action.message_id}
                                toggleMessageId={action.toggle_message_id}
                                colorAction={action.color}
                                colorToggleAction={action.toggle_color}
                                timerAction={action.timer}
                                timerToggleAction={action.toggle_timer}
                                carService={Number(carServiceString)}
                              />
                            )}
                        </div>
                      ))}
                  </div>
                )}
              </div>
            )}
            {/** ******************************************************** */}
            {/** SWITCH BUTTON  */}
            {displaySwitch && (
              <div
                className={`drawer__box__control__panel__${theme} mt-3`}
                style={{ width: "100%" }}
              >
                <div>
                  <Divider
                    orientation="left"
                    className="generalStyles__drawerDividerError"
                  >
                    <h5>
                      <FontAwesomeIcon
                        icon={faToggleOn}
                        className="generalStyles__mrFix generalStyles__success"
                      />
                      SWITCH
                    </h5>
                  </Divider>
                </div>
                {actions && (
                  <div>
                    {actions
                      .slice()
                      .sort((a: BankActionInterface, b: BankActionInterface) =>
                        a.created_at.localeCompare(b.created_at)
                      )
                      .map((action: BankActionInterface, index: number) => (
                        <div className="generalStyles__flexCenter" key={index}>
                          {action &&
                            action.button_type &&
                            action.button_type === "SWITCH" && (
                              <ActionButtonSwitch
                                validStates={action.valid_states}
                                toggleValidStates={action.toggle_valid_states}
                                label={action.label}
                                toggleLabel={action.toggle_label}
                                messageId={action.message_id}
                                toggleMessageId={action.toggle_message_id}
                                colorAction={action.color}
                                timerAction={action.timer}
                                carService={Number(carServiceString)}
                              />
                            )}
                        </div>
                      ))}
                  </div>
                )}
              </div>
            )}
          </>
        ) : (
          <div
            className={`drawer__box__control__panel__${theme} generalStyles__width100 generalStyles__flexCenter mt-3`}
          >
            <Empty
              description={<span>{t("general.noActionsAssigned")}</span>}
            />
          </div>
        )}
      </div>
    </div>
  );
}
